import { EmptyBagMessageCard } from "@/components/Checkout/EmptyBagMessageCard";
import LandingPageWrapper from "@/components/pages/AppPages/Pages/components/LandingPageWrapper";
import { styled } from "@mui/material";
import Loading from "@/components/Loading";
import { useTrackViewEvent } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  brandclub_colors,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { useCallback, useEffect, useState } from "react";
import { useCheckoutSimplifiedUI } from "../CheckoutSimplifiedUIProvider";
import OrderSummary from "./components/OrderSummary";
import { ContactSection } from "./components/ContactSection";
import { ShippingAddressSection } from "./components/ShippingAddressSection";
import { ShippingMethodSection } from "./components/ShippingMethodSection";
import { PaymentSection } from "./components/PaymentSection";
import { RememberMeSection } from "./components/RememberMeSection";
import { Alert, Box, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  minHeight: "100vh",
  gap: "32px",
  padding: "24px 48px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "flex-start",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    flexDirection: "column",
    padding: "16px",
    gap: "24px",
  },

  ".checkoutSubmitForm": {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 640,
    flex: 1,
    overflowY: "auto",
    paddingRight: "16px",
    [theme.breakpoints.down("md")]: {
      paddingRight: 0,
      marginBottom: "80px",
      overflowY: "visible",
    },
    ".collapsible-section + .collapsible-section": {
      borderTop: `1px solid ${brandclub_colors.grayLight}`,
    },
    ".checkoutTitle": {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: 24,
      fontWeight: 600,
      paddingBottom: 16,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        fontSize: 20,
      },
      ".lockIcon": {
        fontSize: 22,
        color: theme.palette.primary.main,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          fontSize: 18,
        },
      },
    },
  },
  ".sideSummary": {
    display: "block",
    position: "sticky",
    top: 24,
    width: "100%",
    maxWidth: 420,
    height: "fit-content",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "block",
      position: "relative",
      top: 0,
      maxWidth: "100%",
      marginTop: "24px",
    },
  },
  ".mobileOrderSummary": {
    display: "none",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "block",
    },
  },
  ".desktopOrderSummary": {
    display: "block",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "none",
    },
  },
}));

export const FormSectionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0",
}));

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  maxHeight: 250,
  maxWidth: "90%",
});

const SuccessContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  gap: "24px",
  textAlign: "center",
  maxWidth: 600,
  margin: "0 auto",
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 80,
  color: theme.palette.success.main,
}));

const CheckoutSimplifiedForm = () => {
  const { session, itemsToCheckout, isLoading, sessionError } =
    useCheckoutSimplifiedUI();

  // Section completion state
  const [completedSections, setCompletedSections] = useState<Set<string>>(
    new Set()
  );

  const trackCheckoutPageView = useTrackViewEvent(
    AnalyticsTrackingEvent.CHECKOUT_PAGE_VIEW
  );
  const trackPageView = useTrackViewEvent(AnalyticsTrackingEvent.PAGE_VIEW);
  const pageView = useCallback(
    (additionalData: Record<string, unknown>) => {
      trackPageView(additionalData);
      trackCheckoutPageView(additionalData);
    },
    [trackCheckoutPageView, trackPageView]
  );

  useEffect(() => {
    const eventParams = {
      checkoutItems: itemsToCheckout,
      checkoutPhase: "pre_submission",
      sessionId: session?.id,
    };
    pageView(eventParams);
  }, [pageView, itemsToCheckout, session?.id]);

  // Determine section completion based on session state
  useEffect(() => {
    if (!session) return;

    const completed = new Set<string>();

    if (session.buyer) {
      completed.add("contact");
    }
    if (session.fulfillment_address) {
      completed.add("shipping");
    }
    if (session.fulfillment_option_id) {
      completed.add("method");
    }
    if (session.status === "completed") {
      completed.add("payment");
    }

    setCompletedSections(completed);
  }, [session]);

  const handleSectionComplete = (section: string) => {
    setCompletedSections((prev) => new Set(prev).add(section));
  };

  const handlePaymentSuccess = () => {
    setCompletedSections((prev) => new Set(prev).add("payment"));
  };

  if (isLoading) {
    return <Loading storeSpin />;
  }

  if (!session) {
    return <StyledEmptyBagMessageCard />;
  }

  // Show success message after order is complete
  if (session.status === "completed" && "order" in session) {
    return (
      <LandingPageWrapper includeFooter={false}>
        <Container>
          <SuccessContainer>
            <SuccessIcon />
            <Typography variant="h4" fontWeight="bold">
              Order Placed Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you for your purchase. Your order #{session.order.id} has
              been confirmed.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href={session.order.permalink_url}
              sx={{ marginTop: 2 }}
            >
              View Order Details
            </Button>
          </SuccessContainer>
        </Container>
      </LandingPageWrapper>
    );
  }

  return (
    <LandingPageWrapper includeFooter={false}>
      <Container>
        <div className="checkoutSubmitForm">
          <div className="checkoutTitle">
            <LockIcon className="lockIcon" />
            Secure Checkout
          </div>

          {sessionError && (
            <Alert
              severity="error"
              sx={{
                marginBottom: 2,
                backgroundColor: "#fee",
                color: "#c00",
                border: "1px solid #fcc",
                "& .MuiAlert-icon": {
                  color: "#c00",
                },
              }}
            >
              {sessionError}
            </Alert>
          )}

          {session.messages && session.messages.length > 0 && (
            <Box sx={{ marginBottom: 2 }}>
              {session.messages.map((message, index) => (
                <Alert
                  key={index}
                  severity={message.type === "error" ? "error" : "info"}
                  sx={{
                    marginBottom: 1,
                    ...(message.type === "error" && {
                      backgroundColor: "#fee",
                      color: "#c00",
                      border: "1px solid #fcc",
                      "& .MuiAlert-icon": {
                        color: "#c00",
                      },
                    }),
                  }}
                >
                  {message.content}
                </Alert>
              ))}
            </Box>
          )}

          <FormSectionContainer>
            <ContactSection
              isOpen={true}
              onToggle={() => {}}
              onComplete={() => handleSectionComplete("contact")}
            />

            <ShippingAddressSection
              isOpen={true}
              onToggle={() => {}}
              onComplete={() => handleSectionComplete("shipping")}
              disabled={!completedSections.has("contact")}
            />

            <ShippingMethodSection
              isOpen={true}
              onToggle={() => {}}
              onComplete={() => handleSectionComplete("method")}
              disabled={!completedSections.has("shipping")}
            />

            <PaymentSection
              isOpen={true}
              onToggle={() => {}}
              onSuccess={handlePaymentSuccess}
              disabled={!completedSections.has("method")}
            />

            <RememberMeSection
              isOpen={true}
              onToggle={() => {}}
              disabled={false}
            />
          </FormSectionContainer>

          <div className="mobileOrderSummary">
            <OrderSummary showOrderSummaryTitle={true} />
          </div>
        </div>

        <div className="sideSummary desktopOrderSummary">
          <OrderSummary showOrderSummaryTitle={true} />
        </div>
      </Container>
    </LandingPageWrapper>
  );
};

export default CheckoutSimplifiedForm;
